import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface ServiceFeature {
  title: string;
  description: string;
}

interface ServiceCardProps {
  title: string;
  description: string;
  image: string;
  alt: string;
  icon: string;
  features: Array<{
    title: string;
    description: string;
  }>;
  priceRange: {
    inr: string;
    aed: string;
    usd: string;
  };
  className?: string;
}

const ServiceCard = ({
  title,
  description,
  image,
  alt,
  icon,
  features,
  priceRange,
  className = '',
}: ServiceCardProps) => {
  return (
    <div className={`bg-card rounded-2xl shadow-natural hover:shadow-natural-lg transition-all duration-300 overflow-hidden border border-border ${className}`}>
      {/* Image Section */}
      <div className="relative h-64 overflow-hidden">
        <AppImage
          src={image}
          alt={alt}
          className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-4 left-4 bg-primary text-primary-foreground p-3 rounded-xl shadow-natural">
          <Icon name={icon as any} size={28} variant="solid" />
        </div>
      </div>

      {/* Content Section */}
      <div className="p-6 md:p-8">
        <h3 className="font-heading font-bold text-2xl text-text-primary mb-3">
          {title}
        </h3>
        <p className="text-text-secondary mb-6 leading-relaxed">
          {description}
        </p>

        {/* Features List */}
        <div className="space-y-3 mb-6">
          {features.map((feature, index) => (
            <div key={index} className="flex items-start space-x-3">
              <Icon
                name="CheckCircleIcon"
                size={20}
                className="text-conversion flex-shrink-0 mt-0.5"
                variant="solid"
              />
              <div>
                <div className="font-semibold text-text-primary text-sm">
                  {feature.title}
                </div>
                <div className="text-text-secondary text-sm">
                  {feature.description}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pricing */}
        <div className="bg-surface rounded-xl p-4 mb-6">
          <div className="text-text-secondary text-sm mb-2">Starting from</div>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-baseline space-x-1">
              <span className="text-text-primary font-bold text-lg">₹</span>
              <span className="text-text-primary font-bold text-xl">
                {priceRange.inr}
              </span>
            </div>
            <div className="flex items-baseline space-x-1">
              <span className="text-text-secondary font-medium text-sm">
                AED {priceRange.aed}
              </span>
            </div>
            <div className="flex items-baseline space-x-1">
              <span className="text-text-secondary font-medium text-sm">
                ${priceRange.usd}
              </span>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <button className="w-full bg-primary text-primary-foreground font-heading font-semibold py-3 px-6 rounded-lg hover:bg-primary/90 transition-colors duration-300 flex items-center justify-center space-x-2">
          <span>Learn More</span>
          <Icon name="ArrowRightIcon" size={20} />
        </button>
      </div>
    </div>
  );
};

export default ServiceCard;